import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Breadcrumb, ProgressBar, Modal } from 'react-bootstrap';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';
import { validateCourseToken } from '../../../utils/urlSecurity';
import { 
  formatDate, 
  formatDuration, 
  getStatusBadgeVariant, 
  getStatusIcon, 
  getSampleImage 
} from '../../../utils/helpers';
import {
  getModuleStatus,
  calculateCourseProgress,
  sortModulesBySequence,
  mapScormTracking,
  prepareModuleCardData,
  prepareCourseHeaderData
} from '../../../utils/courseHelpers';
import scormAPI, { initSCORM, commit, finish, restoreBookmark } from '../../../utils/scormAPI';
import { toast } from 'react-toastify';
// Enhanced with SCORM tracking integration

const IFRAME_LOAD_TIMEOUT_MS = 45000;

const canShowResumeUi = (tracking) => (
  tracking?.lesson_location &&
  tracking.exit_status === 'suspend' &&
  !['completed', 'passed'].includes(tracking.lesson_status)
);

const isReactAppShell = (html) => (
  typeof html === 'string' &&
  html.includes('id="root"') &&
  html.includes('Learning Experience Platform')
);

const validateScormLaunchUrl = async (launchUrl) => {
  try {
    const response = await fetch(launchUrl, { method: 'GET', credentials: 'same-origin' });
    if (!response.ok) {
      return { valid: false, reason: 'not_found' };
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('text/html')) {
      return { valid: true };
    }

    const html = await response.text();
    if (isReactAppShell(html)) {
      return { valid: false, reason: 'misconfigured' };
    }

    return { valid: true };
  } catch (error) {
    console.error('SCORM launch URL validation failed:', error);
    return { valid: false, reason: 'network' };
  }
};

const CourseModulesPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const journeyContext = location.state?.journeyContext;
  const launchModuleId = Number(location.state?.launchModuleId || 0);
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [scormTrackingArray, setScormTrackingArray] = useState([]);
  const [loading, setLoading] = useState({ course: true, modules: true, tracking: true });
  const [error, setError] = useState({ course: null, modules: null, tracking: null });
  const [showModal, setShowModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [iframeError, setIframeError] = useState(false);
  const [iframeErrorReason, setIframeErrorReason] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [scormLaunchUrl, setScormLaunchUrl] = useState(null);
  
  // SCORM tracking state
  const [scormTracking, setScormTracking] = useState({});
  const [trackingInitialized, setTrackingInitialized] = useState(false);
  
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);
  const lastUserIdRef = useRef(null);
  const lastCourseIdRef = useRef(null);
  const certificateIssueAttemptedRef = useRef(false);
  const journeyCompletionAttemptedRef = useRef(false);
  const requestedModuleLaunchedRef = useRef(false);

  // Decrypt course ID
  const decrypted = validateCourseToken(courseId);
  const courseIdValue = decrypted && typeof decrypted === 'object' ? decrypted.courseId : decrypted;

  useEffect(() => {
    // Only fetch data if courseId or userId actually changed
    const currentUserId = user?.id;
    const currentCourseId = courseIdValue;
    
    if (!currentCourseId || !currentUserId) return;
    
    // Check if we've already fetched data for this combination
    if (lastUserIdRef.current === currentUserId && lastCourseIdRef.current === currentCourseId) {
      return;
    }
    
    // Update refs to track current values
    lastUserIdRef.current = currentUserId;
    lastCourseIdRef.current = currentCourseId;
    
    fetchCourse();
    fetchModules();
    fetchScormTrackingData();
    loadScormTrackingData();
  }, [courseIdValue, user?.id, user]);

  // Cleanup timeout and SCORM service on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
      // Cleanup SCORM service - save tracking before leaving
      if (trackingInitialized) {
        commit().catch(console.error);
        finish().catch(console.error);
      }
    };
  }, [trackingInitialized]);

  useEffect(() => {
    certificateIssueAttemptedRef.current = false;
    journeyCompletionAttemptedRef.current = false;
    requestedModuleLaunchedRef.current = false;
  }, [courseIdValue]);

  // Issue certificate when all modules are complete (including retroactive backfill)
  useEffect(() => {
    if (!user?.id || !courseIdValue || modules.length === 0 || loading.tracking) {
      return;
    }

    const progress = calculateCourseProgress(modules, scormTrackingArray);
    if (progress < 100) {
      return;
    }

    if (certificateIssueAttemptedRef.current) {
      return;
    }

    certificateIssueAttemptedRef.current = true;

    api.certificates.issueIfEligible(user.id, courseIdValue)
      .then((response) => {
        if (response.data?.issued) {
          const message = response.data.regenerated
            ? 'Your certificate has been updated! View it in My Certificates.'
            : 'Your course certificate is ready! View it in My Certificates.';
          toast.success(message);
        }
      })
      .catch(() => {
        certificateIssueAttemptedRef.current = false;
      });
  }, [modules, scormTrackingArray, loading.tracking, user?.id, courseIdValue]);

  useEffect(() => {
    if (!journeyContext || modules.length === 0 || loading.tracking) return;
    const progress = calculateCourseProgress(modules, scormTrackingArray);
    if (progress < 100 || journeyCompletionAttemptedRef.current) return;

    journeyCompletionAttemptedRef.current = true;
    api.learningJourneys.completeStep(
      journeyContext.journeyId,
      journeyContext.stepId,
      { course_progress: 100, scorm_progress: 100 }
    ).then(() => {
      toast.success('Course completed — next journey module unlocked');
    }).catch((error) => {
      journeyCompletionAttemptedRef.current = false;
      toast.error(error.response?.data?.message || 'Unable to update journey progress');
    });
  }, [journeyContext, loading.tracking, modules, scormTrackingArray]);

  // Helper function to validate if module can be launched
  const canLaunchModule = (module) => {
    return (module && module.scorm_path && module.status === 1 && !module.deleted);
  };

  

  const buildScormLaunchUrl = (module) => {
    if (!module || !module.scorm_path || !module.launch_file) {
      throw new Error('Module does not have a valid SCORM path');
    }

    const base = (process.env.REACT_APP_SCORM_BASE_URL || '').replace(/\/+$/, '');
    const launchPath = `/scorm/${module.scorm_path}/${module.launch_file}`.replace(/\/{2,}/g, '/');
    return base ? `${base}${launchPath}` : launchPath;
  };

  const fetchCourse = async () => {
    try {
      setLoading(prev => ({ ...prev, course: true }));
      setError(prev => ({ ...prev, course: null }));
      const response = await api.courses.getById(courseIdValue);
      // Handle both new and old API response formats
      const courseData = response.data.success ? response.data.data : response.data;
      setCourse(courseData);
    } catch (error) {
      setError(prev => ({ ...prev, course: 'Failed to load course details' }));
    } finally {
      setLoading(prev => ({ ...prev, course: false }));
    }
  };

  const fetchModules = async () => {
    try {
      setLoading(prev => ({ ...prev, modules: true }));
      setError(prev => ({ ...prev, modules: null }));
      const response = await api.modules.getByCourse(courseIdValue);
      // Extract modules data from response
      const modulesData = response.data.success ? response.data.data : response.data;
      // Sort modules by sequence using helper
      const sortedModules = sortModulesBySequence(modulesData || []);
      setModules(sortedModules);
    } catch (error) {
      let errorMessage = 'Failed to load course modules';
      
      if (error.response?.status === 404) {
        errorMessage = 'Course not found. Please check the course link.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(prev => ({ ...prev, modules: errorMessage }));
    } finally {
      setLoading(prev => ({ ...prev, modules: false }));
    }
  };

  const fetchScormTrackingData = async () => {
    if (!user?.id || !courseIdValue) return;

    try {
      setLoading(prev => ({ ...prev, tracking: true }));
      setError(prev => ({ ...prev, tracking: null }));
      
      // Get all tracking data for current user and course (all modules)
      const response = await api.scormTracking.getCourseTracking(user.id, courseIdValue);
      
      if (response.data.success && response.data.data) {
        setScormTrackingArray(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching SCORM tracking data:', error);
      // Don't set error for tracking as it's optional
    } finally {
      setLoading(prev => ({ ...prev, tracking: false }));
    }
  };

  // Load SCORM tracking data for the course (for current module)
  const loadScormTrackingData = async () => {
    if (!user?.id || !courseIdValue) return;

    try {
      const response = await api.scormTracking.getCourseTracking(user.id, courseIdValue);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const trackingData = response.data.data[0];
        setScormTracking(trackingData);
      } else {
        // No tracking data yet
        setScormTracking({});
      }
    } catch (error) {
      // Not a critical error - user might be starting fresh
      console.warn('⚠️ Could not load SCORM tracking data:', error.message);
      setScormTracking({});
    }
  };

  // Initialize SCORM tracking for a module
  const initializeScormTracking = async (module) => {
    if (!user?.id || !courseIdValue || !module?.id) {
      console.warn('⚠️ Missing required data for SCORM initialization');
      return false;
    }

    try {
      const api = initSCORM({
        version: module.scorm_version || '1.2',
        user_id: user.id,
        course_id: courseIdValue,
        module_id: module.id
      });
      
      if (api) {
        setTrackingInitialized(true);
        
        // Update tracking summary periodically
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
        }
        
        autoSaveIntervalRef.current = setInterval(async () => {
          // Commit current progress
          await commit();
          
          // Refresh tracking data periodically to update UI
          await loadScormTrackingData();
          await fetchScormTrackingData();
        }, 10000); // Update UI every 10 seconds
        
        // Check if this is a resumed session by looking at existing tracking data for this module
        // Find tracking data for the current module
        const moduleTracking = scormTrackingArray.find(t => t.module_id === module.id);
      
        if (moduleTracking && moduleTracking.exit_status === 'suspend') {
          // Restore bookmark to SCORM API (including progress_measure)
          restoreBookmark({
            lesson_location: moduleTracking.lesson_location,
            lesson_status: moduleTracking.lesson_status,
            suspend_data: moduleTracking.suspend_data,
            progress_measure: moduleTracking.progress_measure
          });
          
          toast.info('Resuming from where you left off', {
            position: "top-center",
            autoClose: 5000,
          });
        }
        
        toast.success('Course tracking activated');
        return true;
      } else {
        toast.warn('Course tracking could not be activated');
        return false;
      }
    } catch (error) {
      console.error('❌ Error initializing SCORM tracking:', error);
      
      if (error.response && error.response.status === 401) {
        toast.error('Authentication required. Please refresh the page and try again.');
        // Could also redirect to login here
      } else {
        toast.error('Failed to activate course tracking');
      }
      return false;
    }
  };

  // Finish SCORM tracking session
  const finishScormTracking = async () => {
    if (!trackingInitialized) return;

    try {
      // finish() now calls commit() internally, but let's ensure data is saved
      await commit();
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Then finish the session
      await finish();
      
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
      
      setTrackingInitialized(false);
      toast.info('Course tracking session ended');
      
      // Reload tracking data to show final results
      await loadScormTrackingData();
    } catch (error) {
      console.error('❌ Error finishing SCORM tracking:', error);
    }
  };

  const handleLaunchModule = async (module) => {
    try {
      // Check if module has required data
      if (!module) {
        alert('Module data not available');
        return;
      }

      // Validate module can be launched
      if (!canLaunchModule(module)) {
        alert('Cannot launch module: Module is not available or does not have SCORM content');
        return;
      }

      const launchUrl = buildScormLaunchUrl(module);
      const validation = await validateScormLaunchUrl(launchUrl);

      if (!validation.valid) {
        setSelectedModule(module);
        setScormLaunchUrl(null);
        setShowModal(true);
        setIframeError(true);
        setIframeErrorReason(validation.reason);
        setIframeLoading(false);
        return;
      }

      // Initialize SCORM tracking
      const trackingInitialized = await initializeScormTracking(module);
      if (!trackingInitialized) {
        console.warn('⚠️ SCORM tracking not initialized, continuing without tracking');
      } else {
        // Refresh tracking data after initialization to update UI
        setTimeout(async () => {
          await loadScormTrackingData();
          await fetchScormTrackingData();
        }, 1000);
      }

      setSelectedModule(module);
      setScormLaunchUrl(launchUrl);
      setShowModal(true);
      setIframeError(false);
      setIframeErrorReason(null);
      setIframeLoading(true);
      
      // Allow slow SCORM packages time to load before showing an error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIframeError(true);
        setIframeErrorReason('timeout');
        setIframeLoading(false);
      }, IFRAME_LOAD_TIMEOUT_MS);
    } catch (error) {
      console.error('Error launching module:', error);
      alert(`Failed to launch module: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!launchModuleId || loading.modules || loading.tracking || requestedModuleLaunchedRef.current) return;
    const requestedModule = modules.find((module) => Number(module.id) === launchModuleId);
    if (!requestedModule) return;

    requestedModuleLaunchedRef.current = true;
    handleLaunchModule(requestedModule);
  }, [launchModuleId, loading.modules, loading.tracking, modules]);

  const handleCloseModal = async () => {
    // Suspend SCORM tracking session for bookmarking
    if (trackingInitialized) {
      try {
        
        // Set exit status to suspend and commit final data
        if (scormAPI.api) {
          if (scormAPI.version === '2004') {
            scormAPI.api.SetValue('cmi.exit', 'suspend');
          } else {
            scormAPI.api.LMSSetValue('cmi.core.exit', 'suspend');
          }
          
          // Ensure lesson_location is saved - get current location
          const currentLocation = scormAPI.version === '2004' 
            ? scormAPI.api.GetValue('cmi.location')
            : scormAPI.api.LMSGetValue('cmi.core.lesson_location');
          
          if (currentLocation) {
            // Set it again to ensure it's saved
            if (scormAPI.version === '2004') {
              scormAPI.api.SetValue('cmi.location', currentLocation);
            } else {
              scormAPI.api.LMSSetValue('cmi.core.lesson_location', currentLocation);
            }
          }
        }
        
        // Commit the session (this will trigger tracking save)
        await commit();
        
        // Wait a moment for tracking data to be sent
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
          autoSaveIntervalRef.current = null;
        }
        
        setTrackingInitialized(false);
        
        // Get current location for bookmark
        let bookmark = '';
        if (scormAPI.api) {
          if (scormAPI.version === '2004') {
            bookmark = scormAPI.api.GetValue('cmi.location');
          } else {
            bookmark = scormAPI.api.LMSGetValue('cmi.core.lesson_location');
          }
        }
        
        if (bookmark) {
          toast.success('Progress saved! You can resume where you left off', {
            position: "top-center",
            autoClose: 4000,
          });
        } else {
          toast.info('Progress saved - you can resume later');
        }
        
        // Reload tracking data to show updated results
        await loadScormTrackingData();
        await fetchScormTrackingData();
      } catch (error) {
        console.error('❌ Error suspending SCORM session:', error);
        // Fall back to normal finish
        await finishScormTracking();
      }
    }
    
    setShowModal(false);
    setSelectedModule(null);
    setScormLaunchUrl(null);
    setIframeError(false);
    setIframeErrorReason(null);
    setIframeLoading(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
    setIframeError(false);
    setIframeErrorReason(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Ensure SCORM API is accessible to the iframe content
    if (iframeRef.current && iframeRef.current.contentWindow && trackingInitialized) {
      try {
        const iframeWindow = iframeRef.current.contentWindow;
        
        // Expose SCORM API to iframe
        if (window.API) {
          iframeWindow.API = window.API;
          iframeWindow.parent.API = window.API;
        }
        if (window.API_1484_11) {
          iframeWindow.API_1484_11 = window.API_1484_11;
          iframeWindow.parent.API_1484_11 = window.API_1484_11;
        }
        
        console.log('✅ SCORM API exposed to iframe');
      } catch (error) {
        console.warn('⚠️ Could not expose SCORM API to iframe (cross-origin):', error.message);
      }
    }
  };

  const handleIframeError = () => {
    setIframeError(true);
    setIframeErrorReason('load_failed');
    setIframeLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleOpenInNewTab = () => {
    if (selectedModule) {
      try {
        const launchUrl = buildScormLaunchUrl(selectedModule);
        window.open(launchUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error opening in new tab:', error);
        alert(`Failed to open module: ${error.message}`);
      }
    }
  };

  const getIframeErrorMessage = () => {
    switch (iframeErrorReason) {
      case 'not_found':
        return 'The SCORM package files could not be found on the server. Please contact your administrator or re-upload the module.';
      case 'misconfigured':
        return 'Module content is not being served correctly. The server is returning the app homepage instead of the SCORM files. An administrator needs to configure /scorm/ to serve module files from the backend.';
      case 'timeout':
        return 'The module is taking longer than expected to load. Try opening it in a new tab, or check your network connection.';
      case 'network':
        return 'Could not reach the module content. Please check your connection and try again.';
      default:
        return 'The module content could not be loaded in the player. You can try opening it in a new tab instead.';
    }
  };

  const handleBackToCourses = () => {
    if (journeyContext) {
      navigate(`/user/learning-journeys/${journeyContext.journeyId}`);
      return;
    }
    navigate('/user/enrolled-courses');
  };

  // Calculate overall progress using helper
  const overallProgress = calculateCourseProgress(modules, scormTrackingArray);

  // Prepare course header data using helper
  const courseHeaderData = prepareCourseHeaderData(course, modules, scormTrackingArray);

  // Map SCORM tracking for easy lookup by module ID
  const scormTrackingMap = mapScormTracking(scormTrackingArray);
  
  // Debug logging to verify tracking data
  if (scormTrackingArray.length > 0) {
    console.log('📊 SCORM Tracking Array:', scormTrackingArray);
    console.log('📊 SCORM Tracking Map:', scormTrackingMap);
  }

  return (
    <Container className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <Row>
        <Col>
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item onClick={handleBackToCourses} style={{ cursor: 'pointer' }} className="text-success">
              <i className="fas fa-book me-2"></i> My Courses
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{courseHeaderData.title}</Breadcrumb.Item>
          </Breadcrumb>

          {/* Course Header */}
          {course && (
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
              <Card.Body className="p-4">
                <Row>
                  <Col md={8}>
                    <h4 className="fw-bold text-dark mb-2">
                      <i className="fas fa-graduation-cap me-2 text-success"></i>
                      {courseHeaderData.title}
                    </h4>
                    <p className="text-muted mb-3">{courseHeaderData.description}</p>
                    <div className="d-flex align-items-center mb-3">
                      <Badge bg="info" className="me-3">
                        <i className="fas fa-clock me-1"></i>
                        {courseHeaderData.moduleCount} Module{courseHeaderData.moduleCount !== 1 ? 's' : ''}
                      </Badge>
                      <Badge bg="success">
                        <i className="fas fa-trophy me-1"></i>
                        {courseHeaderData.progressText}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <ProgressBar 
                      now={courseHeaderData.progress} 
                      variant="success" 
                      className="mb-2"
                      style={{ height: '8px' }}
                    />
                    <small className="text-muted">Overall Progress</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Modules Section */}
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Header className="bg-white border-bottom" style={{ borderRadius: '12px 12px 0 0' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="fw-bold text-dark mb-1">
                    <i className="fas fa-route me-2 text-success"></i> Learning Path
                  </h4>
                  <p className="text-muted mb-0">Complete modules in sequence to progress through the course</p>
                </div>
                {modules.length > 0 && (
                  <Badge bg="primary" className="fs-6">
                    {modules.length} Module{modules.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {loading.modules || loading.tracking ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-3">Loading learning path...</p>
                </div>
              ) : error.modules || error.tracking ? (
                <Alert variant="danger" className="mb-0">
                  <i className="fas fa-exclamation-triangle me-2"></i> {error.modules || error.tracking}
                </Alert>
              ) : (
                <div>
                  {modules.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-route text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                      <h5 className="text-muted">No modules available</h5>
                    </div>
                  ) : (
                    <Row>
                      {modules.map((module, index) => {
                        // Use helper to prepare module card data with SCORM tracking
                        const moduleData = prepareModuleCardData(module, index, scormTrackingMap);
                        
                        // Check if module can be launched
                        const canLaunch = canLaunchModule(module);
                        
                        return (
                          <Col key={module.id} lg={6} xl={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                              <div className="position-relative" style={{ height: '200px', overflow: 'hidden', borderRadius: '12px 12px 0 0', backgroundColor: '#f8f9fa' }}>
                                <img 
                                  src={moduleData.sampleImage} 
                                  alt={module.name}
                                  className="card-img-top"
                                  style={{ 
                                    width: '100%',
                                    height: '100%', 
                                    objectFit: 'contain',
                                    objectPosition: 'center'
                                  }}
                                />
                                <div className="position-absolute top-0 end-0 m-2">
                                  <Badge bg={moduleData.badgeVariant} className="fs-6">
                                    <i className={`${moduleData.statusIcon} me-1`}></i>
                                    {moduleData.status}
                                  </Badge>
                                </div>
                              </div>
                              
                              <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <h5 className="fw-bold text-dark mb-2">{module.name}</h5>
                                  <Badge bg="secondary" className="fs-6">
                                    #{moduleData.sequenceNumber}
                                  </Badge>
                                </div>
                                
                                <p className="text-muted mb-3">{module.description}</p>
                                
                                <div className="mb-3">
                                  <div className="d-flex align-items-center"><small className="text-muted me-1">Duration:</small><small className="fw-bold">{moduleData.formattedDuration}</small></div>
                                </div>

                                {moduleData.scormTracking && (
                                  <div className="mb-3">
                                    {moduleData.hasScore && (
                                      <div className="mb-2">
                                        <div className="d-flex align-items-center"><small className="text-muted me-1">Score:</small><small className="fw-bold">{moduleData.score}</small></div>
                                      </div>
                                    )}
                                    
                                    {moduleData.progress > 0 && (
                                      <div className="mb-2">
                                        <small className="text-muted">Progress:</small>
                                        <div className="d-flex align-items-center">
                                          <ProgressBar 
                                            now={moduleData.progress} 
                                            variant="info" 
                                            className="flex-grow-1 me-2"
                                            style={{ height: '6px' }}
                                          />
                                          <small className="fw-bold">{moduleData.progress}%</small>
                                        </div>
                                      </div>
                                    )}

                                    {moduleData.hasTimeSpent && (
                                      <div className="mb-2">
                                        <small className="text-muted d-block">
                                          <i className="fas fa-clock me-1"></i>
                                          Time Spent: <span className="fw-bold">{moduleData.timeSpent}</span>
                                        </small>
                                      </div>
                                    )}

                                    {canShowResumeUi(moduleData.scormTracking) && (
                                      <div className="mb-2">
                                        <small className="text-info d-block">
                                          <i className="fas fa-bookmark me-1"></i>
                                          Progress saved — resume where you left off
                                        </small>
                                      </div>
                                    )}
                                    
                                    <div className="row text-left">
                                      {(moduleData.hasStartDate || moduleData.hasEndDate) && (
                                        <div className="col-12 d-flex justify-content-between">
                                          {moduleData.hasStartDate && (
                                            <div>
                                              <small className="text-muted">First Access: </small>
                                              <small className="fw-bold">{moduleData.formattedStartDate}</small>
                                            </div>
                                          )}
                                          {moduleData.hasEndDate && (
                                            <div>
                                              <small className="text-muted">Completed: </small>
                                              <small className="fw-bold">{moduleData.formattedEndDate}</small>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                 
                                    </div>
                                  </div>
                                )}

                                <Button
                                  bsPrefix="custom-hamburger"
                                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm  flex items-center justify-center gap-2 p-2"
                                  onClick={() => handleLaunchModule(module)}
                                  disabled={!canLaunch}
                                >
                                  {canShowResumeUi(moduleData.scormTracking) ? (
                                    <>
                                      <i className="fas fa-play "></i>
                                      Resume Module
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-play "></i>
                                      {moduleData.status === 'Completed' ? 'Review Module' : 'Launch Module'}
                                    </>
                                  )}
                                </Button>
                                
                                
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Module Content Modal */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal}
        size="xl"
        centered
        className="module-modal"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i className="fas fa-play-circle me-2 text-success"></i>
            {selectedModule?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {scormLaunchUrl && !iframeError ? (
            <div className="position-relative">
              {iframeLoading && (
                <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ zIndex: 10 }}>
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-3">Loading module content...</p>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={scormLaunchUrl}
                title={selectedModule.name}
                style={{
                  width: '100%',
                  height: '80vh',
                  border: 'none',
                  minHeight: '600px',
                  opacity: iframeLoading ? 0.3 : 1,
                  transition: 'opacity 0.3s ease'
                }}
                allow="fullscreen"
                allowFullScreen
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            </div>
          ) : iframeError ? (
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '4rem' }}></i>
              </div>
              <h4 className="text-dark mb-3">Unable to Load Module</h4>
              <p className="text-muted mb-4">
                {getIframeErrorMessage()}
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={handleOpenInNewTab}
                >
                  <i className="fas fa-external-link-alt me-2"></i>
                  Open in New Tab
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="lg"
                  onClick={handleCloseModal}
                >
                  <i className="fas fa-times me-2"></i>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Loading module content...</p>
            </div>
          )}
        </Modal.Body>
        {!iframeError && (
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={handleCloseModal}>
              <i className="fas fa-times me-2"></i>
              Close Module
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </Container>
  );
};

export default CourseModulesPage;